import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  HStack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Form, Formik, FormikHelpers, useFormikContext } from "formik";
import { FC, ReactNode, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { GhostButton, SolidButton } from "~/src/components/common/Button";
import {
  TablePagination,
  useTableLocalPaginationProps,
} from "~/src/components/common/tables/TablePagination";
import { chakraFactory } from "~/src/lib/chakra-ui";

import {
  FunnelImportFormValues,
  normalizeFunnelImportFormValues,
  validationSchema,
} from "../validationSchema";

import { FunnelImportGroup } from "./FunnelImportGroup";

export type Props = {
  siteSlug: string;
  initialFunnels?: FunnelImportFormValues["funnels"];
  onSubmit: (
    values: FunnelImportFormValues,
    helpers: FormikHelpers<FunnelImportFormValues>
  ) => Promise<void>;
};

const StyledSection = chakraFactory(Box, {
  baseStyle: {
    mb: "32px",
  },
});

/** Same widths as popup CSV import sticky delete / row index columns. */
const STICKY_DELETE_W = "44px";
const STICKY_ROW_W = "52px";

// Column header groups for the table
const LP_COLS = [
  "PC用URL必須",
  "モバイル用URL",
  "タブレット用URL",
  "クエリ判定方法",
  "URL一致タイプ",
  "フォームの有無",
  "ホワイトアウト",
  "SPA",
];

const PAGE_COLS = [
  "PC用URL",
  "モバイル用URL",
  "タブレット用URL",
  "クエリ判定方法",
  "URL一致タイプ",
  "ホワイトアウト",
  "SPA",
];

// Header cell for the group row (spans multiple columns)
type GroupThProps = {
  colSpan?: number;
  borderLeft?: boolean;
  children?: ReactNode;
};
function GroupTh({ colSpan, borderLeft = false, children }: GroupThProps) {
  return (
    <Box
      as="th"
      colSpan={colSpan}
      p="6px 8px"
      fontSize="11px"
      fontWeight="bold"
      color="#4A5568"
      bg="#FAFAFA"
      borderBottom="1px solid #CBD5E0"
      borderLeft={borderLeft ? "2px solid #CBD5E0" : undefined}
      textAlign="center"
    >
      {children}
    </Box>
  );
}

// Header cell for the column label row
type ColThProps = {
  borderLeft?: boolean;
  sticky?: boolean;
  /** Second sticky column offset (e.g. line index); implies sticky when set. */
  stickyLeft?: string;
  w?: string;
  minW?: string;
  children?: ReactNode;
};
function ColTh({
  borderLeft = false,
  sticky = false,
  stickyLeft,
  w,
  minW,
  children,
}: ColThProps) {
  const isSticky = sticky || stickyLeft !== undefined;
  return (
    <Box
      as="th"
      p="6px 8px"
      fontSize="11px"
      fontWeight="bold"
      color="#4A5568"
      bg="#FAFAFA"
      borderBottom="2px solid #CBD5E0"
      borderLeft={borderLeft ? "2px solid #CBD5E0" : undefined}
      whiteSpace="nowrap"
      textAlign="center"
      w={w}
      minW={minW}
      position={isSticky ? "sticky" : undefined}
      left={stickyLeft ?? (sticky ? 0 : undefined)}
      zIndex={isSticky ? 2 : undefined}
    >
      {children}
    </Box>
  );
}

type FormContentProps = {
  onCancel: () => void;
};

function FunnelImportFormContent({ onCancel }: FormContentProps) {
  const { values, isSubmitting, setValues, errors } =
    useFormikContext<FunnelImportFormValues>();

  const funnelCount = values.funnels.length;

  const { tableLocalPaginationProps } = useTableLocalPaginationProps({
    totalCount: funnelCount,
    defaultPerPage: 120,
  });

  const handleDeleteFunnel = useCallback(
    (index: number) => {
      const next = values.funnels.filter((_, i) => i !== index);
      setValues({ funnels: next });
    },
    [setValues, values.funnels]
  );

  const visibleFunnels = values.funnels.slice(
    tableLocalPaginationProps.from,
    tableLocalPaginationProps.to
  );

  const hasValidationErrors =
    errors.funnels && Array.isArray(errors.funnels)
      ? errors.funnels.some((e) => e && Object.keys(e).length > 0)
      : !!errors.funnels;

  return (
    <Form>
      <VStack spacing="0" alignItems="stretch">
        {funnelCount > 0 && (
          <StyledSection>
            {hasValidationErrors && (
              <Alert status="error" mb="12px" borderRadius="4px">
                <AlertIcon />
                <AlertDescription fontSize="13px">
                  入力内容にエラーがあります。各フィールドを確認してください。
                </AlertDescription>
              </Alert>
            )}

            <Box mb="8px">
              <Text fontSize="14px" fontWeight="bold">
                ファネル一覧{" "}
                <Text as="span" color="#718096">
                  ({funnelCount}件)
                </Text>
              </Text>
            </Box>

            {/* Pagination top */}
            <Box mb="8px">
              <TablePagination {...tableLocalPaginationProps} />
            </Box>

            {/* Scrollable table */}
            <Box
              overflowX="auto"
              border="1px solid"
              borderColor="#E2E8F0"
              borderRadius="6px"
            >
              <Box
                as="table"
                sx={{ borderCollapse: "collapse" }}
                w="max-content"
                minW="100%"
              >
                <Box as="thead">
                  {/* Group header row */}
                  <Box as="tr" bg="#FAFAFA">
                    {/* Sticky delete column header */}
                    <Box
                      as="th"
                      w={STICKY_DELETE_W}
                      minW={STICKY_DELETE_W}
                      p="6px 8px"
                      borderBottom="1px solid #CBD5E0"
                      position="sticky"
                      left={0}
                      bg="#FAFAFA"
                      zIndex={2}
                    />
                    <Box
                      as="th"
                      w={STICKY_ROW_W}
                      minW={STICKY_ROW_W}
                      p="6px 8px"
                      borderBottom="1px solid #CBD5E0"
                      position="sticky"
                      left={STICKY_DELETE_W}
                      bg="#FAFAFA"
                      zIndex={2}
                    />
                    <GroupTh colSpan={3} borderLeft>
                      ファネル情報
                    </GroupTh>
                    <GroupTh colSpan={8} borderLeft>
                      ランディングページ
                    </GroupTh>
                    <GroupTh colSpan={8} borderLeft>
                      入力フォームページ
                    </GroupTh>
                    <GroupTh colSpan={7} borderLeft>
                      確認ページ
                    </GroupTh>
                    <GroupTh colSpan={7} borderLeft>
                      サンクスページ
                    </GroupTh>
                    <GroupTh borderLeft>テスト用</GroupTh>
                  </Box>

                  {/* Column header row */}
                  <Box as="tr" bg="#FAFAFA">
                    <ColTh sticky w={STICKY_DELETE_W} minW={STICKY_DELETE_W} />
                    <ColTh
                      stickyLeft={STICKY_DELETE_W}
                      w={STICKY_ROW_W}
                      minW={STICKY_ROW_W}
                    >
                      {"\u00a0"}
                    </ColTh>
                    <ColTh borderLeft>アクション</ColTh>
                    <ColTh>ファネルID</ColTh>
                    <ColTh>ファネル名</ColTh>
                    {LP_COLS.map((h, i) => (
                      <ColTh key={`lp-${h}`} borderLeft={i === 0}>
                        {h}
                      </ColTh>
                    ))}
                    {PAGE_COLS.map((h, i) => (
                      <ColTh key={`form-${h}`} borderLeft={i === 0}>
                        {h}
                      </ColTh>
                    ))}
                    <ColTh>
                      フォーム
                      <br />
                      追加/削除
                    </ColTh>
                    {PAGE_COLS.map((h, i) => (
                      <ColTh key={`confirm-${h}`} borderLeft={i === 0}>
                        {h}
                      </ColTh>
                    ))}
                    {PAGE_COLS.map((h, i) => (
                      <ColTh key={`thanks-${h}`} borderLeft={i === 0}>
                        {h}
                      </ColTh>
                    ))}
                    <ColTh borderLeft>テスト用のファネル</ColTh>
                  </Box>
                </Box>

                <Box as="tbody">
                  {visibleFunnels.map((_, pageLocalIndex) => {
                    const absoluteIndex =
                      tableLocalPaginationProps.from + pageLocalIndex;
                    return (
                      <FunnelImportGroup
                        key={absoluteIndex}
                        funnelIndex={absoluteIndex}
                        onDeleteFunnel={handleDeleteFunnel}
                      />
                    );
                  })}
                </Box>
              </Box>
            </Box>

            {/* Pagination bottom */}
            <Box mt="8px">
              <TablePagination {...tableLocalPaginationProps} />
            </Box>
          </StyledSection>
        )}

        {funnelCount === 0 && (
          <Box
            border="2px dashed"
            borderColor="#CBD5E0"
            borderRadius="8px"
            p="40px"
            textAlign="center"
            mb="24px"
          >
            <Text fontSize="14px" color="#718096">
              ファネルがありません。ファネル一覧画面でCSVをアップロードしてください。
            </Text>
          </Box>
        )}

        <HStack justifyContent="flex-end" spacing="16px" mt="8px">
          <GhostButton type="button" onClick={onCancel}>
            戻る
          </GhostButton>

          <SolidButton
            type="submit"
            isDisabled={funnelCount === 0 || isSubmitting}
            isLoading={isSubmitting}
          >
            保存
          </SolidButton>
        </HStack>
      </VStack>
    </Form>
  );
}

export const FunnelImportForm: FC<Props> = ({
  onSubmit,
  siteSlug,
  initialFunnels,
}) => {
  const navigate = useNavigate();

  const handleCancel = useCallback(() => {
    navigate(`/sites/${siteSlug}/settings`);
  }, [navigate, siteSlug]);

  const initialValues = useMemo<FunnelImportFormValues>(
    () => ({ funnels: normalizeFunnelImportFormValues(initialFunnels) }),
    [initialFunnels]
  );

  return (
    <Formik<FunnelImportFormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={onSubmit}
    >
      <FunnelImportFormContent onCancel={handleCancel} />
    </Formik>
  );
};
